import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { ConfirmPopup } from '@neo/blocks/confirm-popup'
import { Button } from '@neo/index'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { Input } from '@neo/primitives/input'
import { Label } from '@neo/primitives/label'
import type { ApiResponse } from '@shared/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { Copy } from 'lucide-react'
import { useState } from 'react'
import { useFlowStore } from 'v2/pages/flow/store'
import { resolveBaseUrl } from 'v2/shared/services/create-service'
import { handoffContextService } from 'v2/shared/services/handoff-context-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { conversationQueryKeys } from 'v2/shared/utils/query-key-management'
import type { HandoffConnection } from 'v2/types/api/handoff-context'
import { errorToast, successToast } from 'v2/utils/toasts'
import { ConnectionSteps } from './handoff-connection-steps'
import { integrationInstructions, uploadUrl } from './handoff-context.helpers'

const adminError = (title: string, action: string) => (error: unknown) =>
  errorToast({
    title,
    description:
      (error as AxiosError).response?.status === 403
        ? `Only admins can ${action}.`
        : getErrorText(error as AxiosError<ApiResponse<unknown>>),
  })

const copy = (text: string, what: string) =>
  navigator.clipboard.writeText(text).then(
    () => successToast({ title: `${what} copied` }),
    () => errorToast({ title: `${what} not copied`, description: 'Select and copy it manually.' }),
  )

interface ConnectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  connection: HandoffConnection
}

/** The organization's AI agent: the name human specialists see, the API key, the endpoint and the steps. */
export const ConnectionDialog = ({ open, onOpenChange, connection }: ConnectionDialogProps) => {
  const url = uploadUrl(resolveBaseUrl())
  const queryClient = useQueryClient()
  // HS stores only the key's hash, so this response is the one time it is shown. Kept outside the dialog
  // content, which unmounts on close, so a key generated while the dialog closes is still shown on reopen.
  const [apiKey, setApiKey] = useState<string>()
  const generate = useMutation({
    mutationFn: async () => (await handoffContextService.regenerateKey()).data,
    onSuccess: (key) => {
      setApiKey(key.api_key)
      return queryClient.invalidateQueries({ queryKey: CONNECTION_QUERY_KEY })
    },
    onError: adminError('API key not generated', 'generate the API key'),
  })
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] max-w-xl grid-cols-[minmax(0,1fr)] overflow-y-auto overflow-x-hidden"
        data-testid="qnr-context-connection"
      >
        <DialogHeader>
          <DialogTitle>Connect your AI agent</DialogTitle>
          <DialogDescription>Shared by every Flow in your organization.</DialogDescription>
        </DialogHeader>
        <NameField connection={connection} />
        {connection.is_admin ? (
          <KeyField
            hasKey={connection.has_key}
            apiKey={apiKey}
            pending={generate.isPending}
            onGenerate={() => generate.mutate()}
          />
        ) : (
          <div className="flex flex-col gap-1.5">
            <Label>API key</Label>
            <span className="text-s-400 text-secondary">Only admins can generate the API key.</span>
          </div>
        )}
        <Input
          label="Endpoint"
          value={url}
          readOnly
          inputClassName="font-jetbrains pr-10"
          endIcon={
            <ClipboardCopyButton textToCopy={url} aria-label="Copy endpoint" tooltipText="Copy" />
          }
          endIconClassName="pr-3"
        />
        <ConnectionSteps url={url} />
        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={() => copy(integrationInstructions(url), 'Setup steps')}
          >
            Copy all steps
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const CONNECTION_QUERY_KEY = conversationQueryKeys().handoffContextConnection

const NameField = ({ connection }: { connection: HandoffConnection }) => {
  const isEditing = useFlowStore((state) => state.isEditing)
  const queryClient = useQueryClient()
  const [name, setName] = useState<string>()
  const draft = name ?? connection.name
  const rename = useMutation({
    mutationFn: async (value: string) => (await handoffContextService.renameConnection(value)).data,
    onSuccess: () => {
      setName(undefined)
      return queryClient.invalidateQueries({ queryKey: CONNECTION_QUERY_KEY })
    },
    onError: adminError('Name not saved', 'change the name'),
  })
  const changed = !!draft.trim() && draft.trim() !== connection.name

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <Input
          label="Name shown to human specialists"
          value={draft}
          readOnly={!connection.is_admin}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      {connection.is_admin && changed && (
        <Button
          type="button"
          variant="outline"
          disabled={!isEditing || rename.isPending}
          onClick={() => rename.mutate(draft.trim())}
        >
          Save
        </Button>
      )}
    </div>
  )
}

const MASK = '••••••••••••••••••••••••••••••••'

interface KeyFieldProps {
  hasKey: boolean
  apiKey?: string
  pending: boolean
  onGenerate: () => void
}

const KeyField = ({ hasKey, apiKey, pending, onGenerate }: KeyFieldProps) => {
  const isEditing = useFlowStore((state) => state.isEditing)
  const disabled = !isEditing || pending

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="handoff-api-key">API key</Label>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <Input
            id="handoff-api-key"
            value={apiKey ?? (hasKey ? MASK : '')}
            placeholder="No API key yet"
            readOnly
            inputClassName="font-jetbrains pr-10"
            data-testid="qnr-context-api-key"
            endIconClassName="pr-1"
            endIcon={
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={!apiKey}
                aria-label="Copy API key"
                onClick={() => apiKey && copy(apiKey, 'API key')}
              >
                <Copy className="size-4" />
              </Button>
            }
          />
        </div>
        {hasKey ? (
          <ConfirmPopup
            title="Regenerate API key?"
            description="The current key stops working immediately. Update your AI agent with the new key."
            okButtonProps={{
              btnTxt: 'Regenerate',
              variant: 'destructive',
              onClick: onGenerate,
            }}
          >
            <Button type="button" variant="outline" disabled={disabled}>
              Regenerate
            </Button>
          </ConfirmPopup>
        ) : (
          <Button type="button" variant="outline" disabled={disabled} onClick={onGenerate}>
            Generate
          </Button>
        )}
      </div>
      <span className="text-xs-400 text-secondary">
        {apiKey
          ? 'Copy it now: it is shown only once.'
          : 'The key is shown only once, when it is generated. Lost it? Regenerate it.'}
      </span>
    </div>
  )
}
